import React, { useRef } from 'react';
import { useRecordContext } from 'react-admin';
import moment from "moment";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";
import ReactToPrint from "react-to-print";
import Button from "@mui/material/Button";
import PrintIcon from "@mui/icons-material/Print";

import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import HomeIcon from "@mui/icons-material/Home";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import WorkIcon from "@mui/icons-material/Work";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import BadgeIcon from "@mui/icons-material/Badge";
import MaleIcon from "@mui/icons-material/Male";
import FemaleIcon from "@mui/icons-material/Female";

import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import PeopleIcon from "@mui/icons-material/People";
import Html from "../uiComponents/Html";
import ImagesWithLightBox from "../uiComponents/ImagesWithLightBox";
import AvatarWithLightBox from "../uiComponents/AvatarWithLightBox";
const SectionHeader = ({ icon, title }) => (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, mt: 4 }}>
        {icon}
        <Typography variant="h6" fontWeight={600}>
            {title}
        </Typography>
        <Box sx={{ flex: 1, ml: 2 }}>
            <Divider />
        </Box>
    </Stack>
);

const InfoRow = ({ icon, label, value }) => {
    if (!value && value !== 0) return null;
    return (
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ py: 0.5 }}>
            <Box sx={{ color: 'text.secondary', mt: '2px', flexShrink: 0 }}>
                {icon}
            </Box>
            <Box>
                {label && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                        {label}
                    </Typography>
                )}
                <Typography variant="body2">{value}</Typography>
            </Box>
        </Stack>
    );
};

const FamilyCard = ({ member }) => {
    const age = moment().diff(moment(member.birthDate), 'years');

    return (
        <Card variant="outlined" sx={{ borderRadius: 3, height: '100%', minWidth: 220 }}>
            <CardContent>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                    <AvatarWithLightBox title={member.name} picture={member.photo} />
                    <Box sx={{ minWidth: 0 }}>
                        <Chip
                            label={member.relationship}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ mb: 0.5, fontWeight: 500 }}
                        />
                        <Typography variant="subtitle1" fontWeight={600} noWrap>
                            {member.name} {member.fatherName || ''}
                        </Typography>
                    </Box>
                </Stack>
                <Divider sx={{ my: 1 }} />
                <Stack spacing={0.25}>
                    <InfoRow icon={<CalendarTodayIcon fontSize="inherit" />} value={`${age} years old`} />
                    {member.health?.generalCondition && (
                        <InfoRow
                            icon={<HealthAndSafetyIcon fontSize="inherit" />}
                            value={member.health.generalCondition}
                        />
                    )}
                    {member.profession && (
                        <InfoRow
                            icon={<WorkIcon fontSize="inherit" />}
                            label="Works as"
                            value={member.profession}
                        />
                    )}
                    {member.monthlyIncome && (
                        <InfoRow
                            icon={<AttachMoneyIcon fontSize="inherit" />}
                            label="Monthly Income"
                            value={`${member.monthlyIncome.toLocaleString()} ETB`}
                        />
                    )}
                    {member.address?.phones?.[0]?.number && (
                        <InfoRow
                            icon={<PhoneIcon fontSize="inherit" />}
                            label="Phone"
                            value={member.address.phones[0].number}
                        />
                    )}
                    {member.address?.email && (
                        <InfoRow
                            icon={<EmailIcon fontSize="inherit" />}
                            label="Email"
                            value={member.address.email}
                        />
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
};

const RecordSummery = ({ source, record: recordProp }) => {
    const recordFromContext = useRecordContext();
    const record = recordProp || recordFromContext;
    const componentRef = useRef();

    if (!record) return null;

    const age = moment().diff(moment(record.birthDate), 'years');
    const GenderIcon = record.gender === 'Male' ? MaleIcon : FemaleIcon;
    const placeAddress = record.place?.address;

    return (
        <Box>
            <Box sx={{ mb: 2 }}>
                <ReactToPrint
                    trigger={() => (
                        <Button variant="outlined" size="small" color="primary" startIcon={<PrintIcon />}>
                            Print / PDF
                        </Button>
                    )}
                    content={() => componentRef.current}
                    pageStyle=""
                />
            </Box>

            <Box ref={componentRef}>

                {/* --- HERO SECTION --- */}
                <Paper elevation={1} sx={{ borderRadius: 3, overflow: 'hidden', mb: 2 }}>
                    <Box sx={{ p: 3 }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems={{ sm: 'flex-start' }}>
                            <AvatarWithLightBox
                                title={record.fullName}
                                picture={record.photo}
                                sx={{ width: 112, height: 112, fontSize: 36, flexShrink: 0 }}
                            />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="h5" fontWeight={700} gutterBottom>
                                    {record.fullName}
                                </Typography>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                                    <Chip icon={<BadgeIcon />} label={record.registrationId} size="small" color="primary" />
                                    {record.official && <Chip label="Official" size="small" color="info" variant="outlined" />}
                                    <Chip icon={<GenderIcon />} label={record.gender} size="small" variant="outlined" />
                                    <Chip icon={<CalendarTodayIcon />} label={`Age: ${age}`} size="small" variant="outlined" />
                                    {record.status && (
                                        <Chip
                                            label={record.status}
                                            size="small"
                                            color={record.status === 'Active' ? 'success' : 'warning'}
                                            variant="outlined"
                                        />
                                    )}
                                </Stack>
                                {record.story?.story && (
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50' }}>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                            Story
                                        </Typography>
                                        <Box sx={{ fontSize: '0.875rem', lineHeight: 1.6 }}>
                                            <Html html={record.story.story} />
                                        </Box>
                                    </Paper>
                                )}
                            </Box>
                        </Stack>
                    </Box>

                    <Divider />

                    <Box sx={{ p: 3, bgcolor: 'grey.50' }}>
                        <Grid container spacing={2}>
                            <Grid item xs={6} sm={4} md={3}>
                                <InfoRow
                                    icon={<CalendarTodayIcon fontSize="small" />}
                                    label="Birth Date"
                                    value={moment(record.birthDate).format('MMMM DD, YYYY')}
                                />
                            </Grid>
                            <Grid item xs={6} sm={4} md={3}>
                                <InfoRow
                                    icon={<CalendarTodayIcon fontSize="small" />}
                                    label="Age"
                                    value={`${age} years`}
                                />
                            </Grid>
                            {record.placeOfBirth && (
                                <Grid item xs={6} sm={4} md={3}>
                                    <InfoRow
                                        icon={<LocationOnIcon fontSize="small" />}
                                        label="Place of Birth"
                                        value={record.placeOfBirth}
                                    />
                                </Grid>
                            )}
                            {record.health?.generalCondition && (
                                <Grid item xs={6} sm={4} md={3}>
                                    <InfoRow
                                        icon={<HealthAndSafetyIcon fontSize="small" />}
                                        label="Health"
                                        value={record.health.generalCondition}
                                    />
                                </Grid>
                            )}
                            {record.address?.phones?.[0]?.number && (
                                <Grid item xs={6} sm={4} md={3}>
                                    <InfoRow
                                        icon={<PhoneIcon fontSize="small" />}
                                        label="Phone"
                                        value={record.address.phones[0].number}
                                    />
                                </Grid>
                            )}
                            {record.address?.email && (
                                <Grid item xs={6} sm={4} md={3}>
                                    <InfoRow
                                        icon={<EmailIcon fontSize="small" />}
                                        label="Email"
                                        value={record.address.email}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                </Paper>

                {/* --- FAMILY SECTION --- */}
                {record.families && record.families.length > 0 && (
                    <>
                        <SectionHeader icon={<PeopleIcon color="primary" />} title="Family Members" />
                        <Grid container spacing={2}>
                            {record.families.map((member, idx) => (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={member._id || idx}>
                                    <FamilyCard member={member} />
                                </Grid>
                            ))}
                        </Grid>
                    </>
                )}

                {/* --- LIVING PLACE SECTION --- */}
                {record.place && (
                    <>
                        <SectionHeader icon={<HomeIcon color="primary" />} title="Living Place" />
                        <Paper elevation={1} sx={{ borderRadius: 3, p: 3 }}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <Stack spacing={1}>
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Typography variant="body2" color="text.secondary">Place Type</Typography>
                                            <Chip label={record.place.type} color="primary" size="small" />
                                            {record.place.type === 'Rental' && record.place.monthlyCost && (
                                                <Typography variant="body2" color="text.secondary">
                                                    {record.place.monthlyCost.toLocaleString()} ETB/month
                                                </Typography>
                                            )}
                                        </Stack>
                                        {record.place.name && (
                                            <Typography variant="subtitle1" fontWeight={600}>
                                                {record.place.name}
                                            </Typography>
                                        )}
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Stack spacing={1}>
                                        {placeAddress?.city && (
                                            <InfoRow icon={<LocationOnIcon fontSize="small" />} value={placeAddress.city} label="City" />
                                        )}
                                        {placeAddress?.subcity && (
                                            <InfoRow icon={<LocationOnIcon fontSize="small" />} value={placeAddress.subcity} label="Subcity" />
                                        )}
                                        {placeAddress?.woreda && (
                                            <InfoRow icon={<LocationOnIcon fontSize="small" />} value={`Woreda ${placeAddress.woreda}`} label="Woreda" />
                                        )}
                                        {placeAddress?.locationText && (
                                            <InfoRow icon={<LocationOnIcon fontSize="small" />} value={placeAddress.locationText} label="Location" />
                                        )}
                                        {placeAddress?.houseNumber && (
                                            <InfoRow icon={<HomeIcon fontSize="small" />} value={placeAddress.houseNumber} label="House No." />
                                        )}
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Paper>
                    </>
                )}

                {/* --- STORY PHOTOS --- */}
                {record.story?.photos && record.story.photos.filter(p => p?.filePath).length > 0 && (
                    <>
                        <SectionHeader icon={<PhotoLibraryIcon color="primary" />} title="Story Photos" />
                        <Paper elevation={0} variant="outlined" sx={{ borderRadius: 3, p: 2 }}>
                            <ImagesWithLightBox photos={record.story.photos} />
                        </Paper>
                    </>
                )}

            </Box>
        </Box>
    );
};

export default RecordSummery;
